import {
    Badge,
    Banner,
    Button,
    ChoiceList,
    IndexTable,
    RadioButton,
    Text,
    Thumbnail,
} from '@shopify/polaris';
import type { IndexFiltersProps } from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import type {
    CollectionMappingListItem,
    CollectionMappingListResult,
    SelectedCollectionMapping,
} from '../../app/models/CollectionMapping';
import type { FitmentFieldValues } from '../../app/utils/fitmentFields';
import { PageQueryCache, isBrowseRequestCancelled } from './PageQueryCache';
import MappingFilters from './MappingFilters';

interface CollectionMappingsPanelProps {
    cache: PageQueryCache<CollectionMappingListResult>;
    onMappingSaved?: () => void;
    hasVehicleContext: boolean;
    selectedVehicleLabel: string;
    year: string;
    yearTo: string;
    make: string;
    model: string;
    submodel: string;
    fieldValues: FitmentFieldValues;
    onDirtyChange?: (dirty: boolean) => void;
}

const emptyMapping = (): SelectedCollectionMapping => ({
    state: 'unmapped',
    collectionId: null,
    collectionTitle: '',
    collectionHandle: '',
    selectedVehicleCount: 0,
    mappedVehicleCount: 0,
});

const showToast = (message: string) => window.shopify?.toast?.show?.(message);

/**
 * Collection-side mapping surface. The selected vehicle is owned by the parent
 * Products page, while this component owns collection browsing and the single
 * mapping draft. Keeping those responsibilities separate prevents product
 * bulk-selection behavior from leaking into collection mapping.
 */
const CollectionMappingsPanel: React.FC<CollectionMappingsPanelProps> = ({
    cache,
    onMappingSaved,
    hasVehicleContext,
    selectedVehicleLabel,
    year,
    yearTo,
    make,
    model,
    submodel,
    fieldValues,
    onDirtyChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [collections, setCollections] = useState<CollectionMappingListItem[]>([]);
    const [mapping, setMapping] = useState<SelectedCollectionMapping>(emptyMapping);
    const [draftCollectionId, setDraftCollectionId] = useState<number | null>(null);
    const [draftChanged, setDraftChanged] = useState(false);
    const [query, setQuery] = useState('');
    const [collectionType, setCollectionType] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState(['title asc']);
    const [mappedOnly, setMappedOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const latestRequestId = useRef(0);
    const previousSelectionKey = useRef('');
    const draftChangedRef = useRef(draftChanged);

    useEffect(() => {
        draftChangedRef.current = draftChanged;
        onDirtyChange?.(draftChanged);
    }, [draftChanged, onDirtyChange]);

    const selectionKey = useMemo(() => JSON.stringify({
        year,
        yearTo,
        make,
        model,
        submodel,
        fields: Object.entries(fieldValues).sort(([left], [right]) => left.localeCompare(right)),
    }), [fieldValues, make, model, submodel, year, yearTo]);

    const loadCollections = useCallback(async (resetDraft: boolean) => {
        const requestId = ++latestRequestId.current;
        const cacheKey = JSON.stringify({
            page, query, mappedOnly, selectionKey, sort: sortSelected[0], collectionType: collectionType[0] ?? '',
        });
        setLoading(cache.get(cacheKey) === undefined);
        try {
            const result = await cache.load(cacheKey, signal => agent.CollectionMappings.list(
                page,
                query,
                mappedOnly,
                year,
                yearTo,
                make,
                model,
                submodel,
                fieldValues,
                sortSelected[0],
                collectionType[0] ?? '',
                signal,
            ));
            if (requestId !== latestRequestId.current) return;

            setCollections(result.collections);
            setMapping(result.mapping);
            setHasNext(result.hasNext);
            setTotalCount(result.totalCount);
            if (resetDraft || !draftChangedRef.current) {
                setDraftCollectionId(
                    result.mapping.state === 'mapped'
                        ? result.mapping.collectionId ?? null
                        : null,
                );
                setDraftChanged(false);
            }
        } catch (error: any) {
            if (requestId === latestRequestId.current && !isBrowseRequestCancelled(error)) {
                showToast(error?.message ?? 'Failed to load collections');
            }
        } finally {
            if (requestId === latestRequestId.current) setLoading(false);
        }
    }, [cache, selectionKey, collectionType, fieldValues, make, mappedOnly, model, page, query, sortSelected, submodel, year, yearTo]);

    useEffect(() => {
        const selectionChanged = previousSelectionKey.current !== selectionKey;
        previousSelectionKey.current = selectionKey;

        if (selectionChanged && page !== 1) {
            setPage(1);
            return;
        }

        if (selectionChanged) {
            setMappedOnly(false);
            setDraftChanged(false);
        }

        loadCollections(selectionChanged);
        return () => {
            latestRequestId.current += 1;
            cache.cancel();
        };
    }, [cache, loadCollections, page, selectionKey]);

    const selectCollection = useCallback((collectionId: number) => {
        if (!hasVehicleContext) {
            showToast('Select a vehicle first.');
            return;
        }
        setDraftCollectionId(collectionId);
        setDraftChanged(true);
    }, [hasVehicleContext]);

    const removeMapping = useCallback(() => {
        if (!hasVehicleContext) return;
        setDraftCollectionId(null);
        setDraftChanged(true);
    }, [hasVehicleContext]);

    const discardChanges = useCallback(() => {
        setDraftCollectionId(mapping.state === 'mapped' ? mapping.collectionId ?? null : null);
        setDraftChanged(false);
    }, [mapping]);

    const saveMapping = useCallback(async () => {
        if (!hasVehicleContext || !draftChanged) return;

        setSaving(true);
        try {
            const saved = await agent.CollectionMappings.saveMapping({
                collectionId: draftCollectionId,
                year,
                yearTo,
                make,
                model,
                submodel,
                fieldValues,
            });
            cache.clear();
            onMappingSaved?.();
            setMapping(saved);
            setDraftCollectionId(saved.collectionId ?? null);
            setDraftChanged(false);
            showToast(saved.state === 'mapped'
                ? `Collection mapping saved for ${selectedVehicleLabel}`
                : `Collection mapping removed for ${selectedVehicleLabel}`);
            await loadCollections(true);
        } catch (error: any) {
            cache.clear();
            onMappingSaved?.();
            showToast(error?.message ?? 'Failed to save collection mapping');
        } finally {
            setSaving(false);
        }
    }, [
        cache,
        onMappingSaved,
        draftChanged,
        draftCollectionId,
        fieldValues,
        hasVehicleContext,
        loadCollections,
        make,
        model,
        selectedVehicleLabel,
        submodel,
        year,
        yearTo,
    ]);

    const headings: React.ComponentProps<typeof IndexTable>['headings'] = hasVehicleContext
        ? [
            { title: 'Mapped' },
            { title: 'Collection' },
            { title: 'Handle' },
            { title: 'Products' },
            { title: 'Assigned vehicles' },
        ]
        : [
            { title: 'Collection' },
            { title: 'Handle' },
            { title: 'Products' },
            { title: 'Assigned vehicles' },
        ];

    const mappingSummary = (() => {
        if (!hasVehicleContext) {
            return null;
        }

        if (mapping.state === 'mixed') {
            return (
                <Banner tone="warning" title="Selected range has mixed collection mappings">
                    {mapping.mappedVehicleCount} of {mapping.selectedVehicleCount} vehicles are mapped to one or more collections.
                    Choosing a collection below will map every vehicle in the selected range to the same collection.
                </Banner>
            );
        }

        return (
            <div className="pf-products-vehicle-context">
                <div className="pf-products-vehicle-context__summary">
                    <div className="pf-products-vehicle-context__details">
                        <Text as="p" variant="bodySm" tone="subdued">Selected vehicle</Text>
                        <div className="pf-products-vehicle-context__vehicle">
                            <Text as="p" variant="bodyMd" fontWeight="semibold">{selectedVehicleLabel}</Text>
                            {mapping.state === 'mapped' ? (
                                <Badge tone="success">{`Mapped to “${mapping.collectionTitle}”`}</Badge>
                            ) : (
                                <Badge>No collection mapping</Badge>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    })();

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Collection title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Collection title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Products', value: 'products asc', directionLabel: 'Lowest to highest' },
        { label: 'Products', value: 'products desc', directionLabel: 'Highest to lowest' },
        { label: 'Assigned vehicles', value: 'vehicles asc', directionLabel: 'Lowest to highest' },
        { label: 'Assigned vehicles', value: 'vehicles desc', directionLabel: 'Highest to lowest' },
    ];

    const clearCollectionType = () => { setCollectionType([]); setPage(1); };
    const clearQuery = () => { setQuery(''); setPage(1); };
    const filters: IndexFiltersProps['filters'] = [{
        key: 'collectionType',
        label: 'Collection type',
        filter: (
            <ChoiceList
                title="Collection type"
                titleHidden
                choices={[{ label: 'Manual', value: 'manual' }, { label: 'Smart', value: 'smart' }]}
                selected={collectionType}
                onChange={(value) => { setCollectionType(value); setPage(1); }}
            />
        ),
        shortcut: true,
    }];
    const appliedFilters: IndexFiltersProps['appliedFilters'] = collectionType.length > 0 ? [{
        key: 'collectionType',
        label: `Collection type: ${collectionType[0] === 'manual' ? 'Manual' : 'Smart'}`,
        onRemove: clearCollectionType,
    }] : [];

    return (
        <>
            <SaveBar id="collection-mapping-save-bar" open={draftChanged}>
                <button onClick={discardChanges} disabled={loading || saving}>Discard</button>
                <button variant="primary" onClick={saveMapping} disabled={loading || saving}>Save</button>
            </SaveBar>

            <div className="pf-collection-mappings">
                {mappingSummary}

                {hasVehicleContext && (
                    <div className="pf-collection-mappings__mode">
                        <div>
                            <Text as="p" variant="bodyMd" fontWeight="semibold">Collection mapping</Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                                Associate the selected vehicle with one Shopify collection. Product mappings remain independent.
                            </Text>
                        </div>
                        <Button
                            onClick={removeMapping}
                            variant="secondary"
                            disabled={loading
                                || saving
                                || (draftCollectionId == null && (mapping.state === 'unmapped' || draftChanged))}
                        >
                            Remove mapping
                        </Button>
                    </div>
                )}

                <div>
                    <MappingFilters
                        hasVehicleContext={hasVehicleContext}
                        mappedOnly={mappedOnly}
                        onMappedOnlyChange={(value) => { setMappedOnly(value); setPage(1); }}
                        sortOptions={sortOptions}
                        sortSelected={sortSelected}
                        onSort={(value) => { setSortSelected(value); setPage(1); }}
                        loading={loading}
                        tabs={[]}
                        selected={0}
                        canCreateNewView={false}
                        queryValue={query}
                        queryPlaceholder="Search collections"
                        onQueryChange={(value) => { setQuery(value); setPage(1); }}
                        onQueryClear={clearQuery}
                        onClearAll={() => { clearQuery(); clearCollectionType(); }}
                        filters={filters}
                        appliedFilters={appliedFilters}
                    />
                </div>
                <IndexTable
                    selectable={false}
                    resourceName={{ singular: 'collection', plural: 'collections' }}
                    itemCount={collections.length}
                    headings={headings}
                    loading={loading}
                    pagination={{
                        label: `${totalCount.toLocaleString()} collection${totalCount === 1 ? '' : 's'}`,
                        hasNext,
                        hasPrevious: page > 1,
                        onNext: () => setPage(current => current + 1),
                        onPrevious: () => setPage(current => Math.max(1, current - 1)),
                    }}
                >
                    {collections.map((collection, index) => (
                        <IndexTable.Row
                            id={String(collection.id)}
                            key={collection.id}
                            position={index}
                            onClick={() => selectCollection(collection.id)}
                        >
                            {hasVehicleContext && (
                                <IndexTable.Cell>
                                    <div onClick={event => event.stopPropagation()}>
                                        <RadioButton
                                            id={`collection-mapping-${collection.id}`}
                                            label={`Map ${selectedVehicleLabel} to ${collection.title}`}
                                            labelHidden
                                            checked={draftCollectionId === collection.id}
                                            onChange={() => selectCollection(collection.id)}
                                            disabled={loading || saving}
                                        />
                                    </div>
                                </IndexTable.Cell>
                            )}
                            <IndexTable.Cell>
                                <button
                                    type="button"
                                    className="pf-collection-mappings__collection pf-collection-mappings__collection--link"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/products/collections/${collection.id}`, {
                                            state: { from: `${location.pathname}${location.search}` },
                                        });
                                    }}
                                >
                                    <Thumbnail
                                        source={collection.imageUrl || ImageIcon}
                                        alt={collection.title}
                                        size="small"
                                    />
                                    <div>
                                        <Text as="p" variant="bodyMd" fontWeight="semibold">{collection.title}</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {collection.collectionType || 'Shopify collection'}
                                        </Text>
                                    </div>
                                </button>
                            </IndexTable.Cell>
                            <IndexTable.Cell>{collection.handle ? `/${collection.handle}` : '-'}</IndexTable.Cell>
                            <IndexTable.Cell>{collection.productsCount.toLocaleString()}</IndexTable.Cell>
                            <IndexTable.Cell>{collection.assignedVehicleCount.toLocaleString()}</IndexTable.Cell>
                        </IndexTable.Row>
                    ))}
                </IndexTable>
            </div>
        </>
    );
};

export default CollectionMappingsPanel;
